import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Input, Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import { SignedImg, SignedVideo } from '@shared/ui/SignedMedia';
import { currentLocale } from '@shared/i18n';

interface SocialAccountRow {
  id: string;
  handle: string;
  bio: string | null;
  is_business: boolean | null;
  ig_category_name: string | null;
  ambassador_brand_ids: string[] | null;
  ambassador_handles_raw: string[] | null;
  niche_ids: string[] | null;
  content_languages: string[] | null;
  full_name: string | null; // creator display name (moved off the dropped providers table)
  verified: boolean | null;  // scraped IG blue check
  creator_claims: Array<{ status: string | null; verified_at: string | null }> | null; // ownership claims
}

interface NicheRow {
  id: string;
  name_ru: string;
  name_en: string | null;
}

interface PostRow {
  id: string;
  post_type: string | null;     // 'image' | 'carousel' | 'reel' | 'video' | 'story'
  caption: string | null;
  likes: number | null;
  comments: number | null;
  views: number | null;
  posted_at: string | null;
  permalink: string | null;
  thumb_public_url: string | null;
  thumbnail_url: string | null;
  video_public_url: string | null;
  child_posts: unknown | null;  // { slides: [{url, public_url, mirrored_at}] } or array
  is_coauthored?: boolean | null;
  owner_handle?: string | null;
}

interface StoryRow {
  id: string;
  ig_story_id: string | null;
  media_type: string | null;
  caption: string | null;
  taken_at: string | null;
  expires_at: string | null;
  view_count: number | null;
  thumb_public_url: string | null;
  thumbnail_url: string | null;
  media_url: string | null;
}

interface MentionAggRow {
  brand_id: string;
  brand_name: string | null;
  mentions: number;
}

interface BrandRow {
  id: string;
  name: string;
}

interface Props {
  socialAccountId: string;
  onClose: () => void;
}

export default function CreatorDetailModal({ socialAccountId, onClose }: Props) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();

  const stableClose = useCallback(() => onClose(), [onClose]);
  useCloseOnTabSwitch(stableClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const accountQ = useSupabaseQuery<SocialAccountRow>(
    ['admin-creator', socialAccountId],
    async () =>
      supabase
        .from('instagram_providers')
        .select('id, handle, bio, is_business, ig_category_name, ambassador_brand_ids, ambassador_handles_raw, niche_ids, content_languages, full_name, verified, creator_claims(status, verified_at)')
        .eq('id', socialAccountId)
        .single(),
  );
  const account: SocialAccountRow | null = (accountQ.data as SocialAccountRow | null) ?? null;

  // Owned + co-authored posts (one canonical row shown on every participant's grid).
  // The RPC already excludes story-typed rows and resolves co-authorship.
  const postsQ = useSupabaseQuery<PostRow[]>(
    ['admin-creator-posts', socialAccountId],
    async () => supabase.rpc('get_creator_grid_posts', { p_provider_id: socialAccountId, p_limit: 24 }),
  );

  const storiesQ = useSupabaseQuery<StoryRow[]>(
    ['admin-creator-stories', socialAccountId],
    async () =>
      supabase
        .from('ig_stories')
        .select('id, ig_story_id, media_type, caption, taken_at, expires_at, view_count, thumb_public_url, thumbnail_url, media_url')
        .eq('instagram_provider_id', socialAccountId)
        .order('taken_at', { ascending: false })
        .limit(24),
  );

  const mentionsQ = useSupabaseQuery<MentionAggRow[]>(
    ['admin-creator-mentions', socialAccountId],
    async () => {
      // Brand mentions live in two tables — ig_post_brand_mentions and
      // ig_story_brand_mentions. We need both, otherwise creators whose
      // brand activity is mostly in stories show inflated zeros (e.g.
      // shabdanova.aidai had 5 Beauty-showroom mentions across 4 stories
      // + 1 post; querying only posts surfaced 1).
      const [postsRes, storiesRes] = await Promise.all([
        supabase
          .from('ig_post_brand_mentions')
          .select('post_id, brand_id, brands(name), ig_posts!inner(instagram_provider_id)')
          .eq('ig_posts.instagram_provider_id', socialAccountId)
          .gte('confidence', 0.7),
        supabase
          .from('ig_story_brand_mentions')
          .select('story_id, brand_id, brands(name), ig_stories!inner(instagram_provider_id)')
          .eq('ig_stories.instagram_provider_id', socialAccountId)
          .gte('confidence', 0.7),
      ]);
      if (postsRes.error) return postsRes;
      if (storiesRes.error) return storiesRes;
      // Count distinct (post_id|story_id) per brand. A single post matched
      // via several sources (usertag + mention etc.) collapses to one.
      const seen = new Map<string, Set<string>>();
      const names = new Map<string, string | null>();
      const pickName = (b: { name: string | null } | { name: string | null }[] | null) =>
        Array.isArray(b) ? b[0]?.name ?? null : b?.name ?? null;
      type PostJoin = { post_id: string; brand_id: string; brands: { name: string | null } | { name: string | null }[] | null };
      type StoryJoin = { story_id: string; brand_id: string; brands: { name: string | null } | { name: string | null }[] | null };
      for (const r of (postsRes.data ?? []) as PostJoin[]) {
        if (!r.brand_id || !r.post_id) continue;
        names.set(r.brand_id, pickName(r.brands));
        const set = seen.get(r.brand_id) ?? new Set<string>();
        set.add(`p:${r.post_id}`);
        seen.set(r.brand_id, set);
      }
      for (const r of (storiesRes.data ?? []) as StoryJoin[]) {
        if (!r.brand_id || !r.story_id) continue;
        names.set(r.brand_id, pickName(r.brands));
        const set = seen.get(r.brand_id) ?? new Set<string>();
        set.add(`s:${r.story_id}`);
        seen.set(r.brand_id, set);
      }
      const rows: MentionAggRow[] = Array.from(seen.entries())
        .map(([brand_id, refs]) => ({ brand_id, brand_name: names.get(brand_id) ?? null, mentions: refs.size }))
        .sort((a, b) => b.mentions - a.mentions);
      return { data: rows, error: null };
    },
  );

  const ambassadorIds = account?.ambassador_brand_ids ?? [];

  const ambassadorBrandsQ = useSupabaseQuery<BrandRow[]>(
    ['admin-creator-ambassador-brands', ambassadorIds.join(',')],
    async () => {
      if (ambassadorIds.length === 0) return { data: [], error: null };
      return supabase.from('brands').select('id, name').in('id', ambassadorIds);
    },
  );
  const ambassadorBrands = ambassadorBrandsQ.data ?? [];

  // Followers / ER come from the latest profile snapshot (canonical), not the retired
  // providers.platforms blob.
  const snapshotQ = useSupabaseQuery<{ followers: number | null; engagement_rate: number | null } | null>(
    ['admin-creator-snap', socialAccountId],
    async () =>
      supabase
        .from('ig_profile_snapshots')
        .select('followers, engagement_rate')
        .eq('instagram_provider_id', socialAccountId)
        .order('scraped_date', { ascending: false })
        .limit(1)
        .maybeSingle(),
  );
  const followers = snapshotQ.data?.followers ?? null;
  const engagement = snapshotQ.data?.engagement_rate != null ? Number(snapshotQ.data.engagement_rate) : null;

  const setAmbassadors = async (next: string[]) => {
    const { error } = await supabase.rpc('admin_set_creator_ambassadors', {
      p_instagram_provider_id: socialAccountId,
      p_brand_ids: next,
    });
    if (error) {
      alert(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ['admin-creator', socialAccountId] });
    qc.invalidateQueries({ queryKey: ['admin-creator-ambassador-brands'] });
  };

  const nicheIds = account?.niche_ids ?? [];
  const allNichesQ = useSupabaseQuery<NicheRow[]>(
    ['admin-niches-all'],
    async () =>
      supabase
        .from('niches')
        .select('id, name_ru, name_en')
        .eq('is_active', true)
        .order('name_ru', { ascending: true }),
  );
  const allNiches = allNichesQ.data ?? [];
  const selectedNiches = useMemo(
    () => allNiches.filter((n) => nicheIds.includes(n.id)),
    [allNiches, nicheIds],
  );

  const setNiches = async (next: string[]) => {
    const { error } = await supabase.rpc('admin_set_creator_niches', {
      p_instagram_provider_id: socialAccountId,
      p_niche_ids: next,
    });
    if (error) {
      alert(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ['admin-creator', socialAccountId] });
  };

  const languages = account?.content_languages ?? [];
  const setLanguages = async (next: string[]) => {
    const { error } = await supabase.rpc('admin_set_creator_languages', {
      p_instagram_provider_id: socialAccountId,
      p_languages: next,
    });
    if (error) {
      alert(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ['admin-creator', socialAccountId] });
  };

  return (
    <div
      role="dialog" aria-modal="true" onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 16px', overflowY: 'auto', backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1100, background: 'var(--bg-elev, #161b22)',
          border: '1px solid var(--border-strong, #2a2d35)', borderRadius: 14,
          padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <SectionErrorBoundary name="admin-creator-detail">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-caption)', textTransform: 'uppercase', letterSpacing: 0.5, color: '#9aa' }}>
                {t('creator.kicker', { defaultValue: 'Instagram creator' })}
              </div>
              <h2 style={{ margin: '4px 0 0', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', display: 'flex', alignItems: 'center', gap: 8 }}>
                @{account?.handle ?? '…'}
                <VerifiedBadge
                  size={20}
                  verified={account?.verified}
                  claimed={(account?.creator_claims ?? []).some((c) => c.status === 'verified' || c.verified_at != null)}
                />
              </h2>
              {account?.full_name && (
                <div style={{ marginTop: 4, fontSize: 'var(--fs-body)', color: '#bcd0ff' }}>{account.full_name}</div>
              )}
              <div style={{ marginTop: 6, fontSize: 'var(--fs-body)', color: '#9aa', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {followers != null && <span>{followers.toLocaleString()} {t('creator.followers', { defaultValue: 'followers' })}</span>}
                {engagement != null && <span>{engagement}% {t('creator.engagement', { defaultValue: 'engagement' })}</span>}
                {account?.ig_category_name && <span>· {account.ig_category_name}</span>}
                {account?.is_business && <span>· {t('creator.business', { defaultValue: 'business' })}</span>}
              </div>
            </div>
            <button
              type="button" onClick={onClose}
              aria-label={t('creator.close', { defaultValue: 'Close' })}
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#dde', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 'var(--fs-label)', fontFamily: 'inherit' }}
            >×</button>
          </div>

          {accountQ.isLoading ? (
            <Skeleton height={160} />
          ) : accountQ.error ? (
            <div style={{ color: '#e74c3c' }}>{String((accountQ.error as Error)?.message ?? accountQ.error)}</div>
          ) : (
            <>
              {/* Bio */}
              <Section title={t('creator.bio', { defaultValue: 'Bio' })}>
                {account?.bio ? (
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-body)', color: '#dde', lineHeight: 1.5 }}>{account.bio}</div>
                ) : (
                  <div style={{ fontSize: 'var(--fs-body)', color: '#778' }}>
                    {t('creator.bioEmpty', { defaultValue: 'No bio captured yet — runs after the next scrape.' })}
                  </div>
                )}
              </Section>

              {/* Ambassadors */}
              <Section title={t('creator.ambassadors', { defaultValue: 'Brand ambassador of' })}>
                <AmbassadorEditor
                  brands={ambassadorBrands}
                  ambassadorIds={ambassadorIds}
                  rawHandles={account?.ambassador_handles_raw ?? []}
                  onSet={setAmbassadors}
                />
              </Section>

              {/* Niches */}
              <Section title={t('creator.niches', { defaultValue: 'Niches' })}>
                <NicheEditor
                  allNiches={allNiches}
                  selected={selectedNiches}
                  onSet={setNiches}
                />
              </Section>

              {/* Content languages */}
              <Section title={t('creator.languages', { defaultValue: 'Content languages' })}>
                <LanguageEditor selected={languages} onSet={setLanguages} />
              </Section>

              {/* Brand mentions */}
              <Section title={t('creator.brandMentions', { defaultValue: 'Brand mentions in posts' })}>
                {mentionsQ.isLoading ? <Skeleton height={48} /> : mentionsQ.data && mentionsQ.data.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {mentionsQ.data.map((m) => (
                      <span key={m.brand_id} style={{ padding: '4px 10px', borderRadius: 12, background: 'rgba(74,158,255,0.1)', color: '#bcd0ff', fontSize: 'var(--fs-small)', border: '1px solid rgba(74,158,255,0.25)' }}>
                        {m.brand_name ?? '?'} · {m.mentions}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--fs-body)', color: '#778' }}>{t('creator.noMentions', { defaultValue: 'No brand mentions detected.' })}</div>
                )}
              </Section>

              {/* Recent stories (rendered first because they're ephemeral / fresh signal) */}
              <Section title={t('creator.recentStories', { defaultValue: 'Recent stories' })}>
                {storiesQ.isLoading ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={180} />)}
                  </div>
                ) : storiesQ.data && storiesQ.data.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                    {storiesQ.data.map((s) => <StoryThumb key={s.id} s={s} />)}
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--fs-body)', color: '#778' }}>{t('creator.noStories', { defaultValue: 'No stories scraped yet.' })}</div>
                )}
              </Section>

              {/* Recent posts */}
              <Section title={t('creator.recentPosts', { defaultValue: 'Recent posts' })}>
                {postsQ.isLoading ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                    {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={160} />)}
                  </div>
                ) : postsQ.data && postsQ.data.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                    {postsQ.data.map((p) => <PostThumb key={p.id} p={p} />)}
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--fs-body)', color: '#778' }}>{t('creator.noPosts', { defaultValue: 'No posts scraped yet.' })}</div>
                )}
              </Section>
            </>
          )}
        </SectionErrorBoundary>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 'var(--fs-caption)', textTransform: 'uppercase', letterSpacing: 0.5, color: '#9aa', marginBottom: 8 }}>{title}</div>
      {children}
    </section>
  );
}

/** Extract carousel slide image URLs from child_posts JSON (handles both shapes). */
function extractCarouselSlides(childPosts: unknown): string[] {
  if (!childPosts) return [];
  // Shape A: mirrored — { slides: [{ url, public_url, mirrored_at }] }
  if (typeof childPosts === 'object' && childPosts !== null && Array.isArray((childPosts as { slides?: unknown[] }).slides)) {
    const out: string[] = [];
    for (const s of (childPosts as { slides: Array<Record<string, unknown>> }).slides) {
      const u = (s.public_url ?? s.url) as string | undefined;
      if (u) out.push(u);
    }
    return out;
  }
  // Shape B: { images: [...urls] }
  if (typeof childPosts === 'object' && childPosts !== null && Array.isArray((childPosts as { images?: unknown[] }).images)) {
    return ((childPosts as { images: unknown[] }).images.filter((u): u is string => typeof u === 'string'));
  }
  // Shape C: raw array of slide objects
  if (Array.isArray(childPosts)) {
    const out: string[] = [];
    for (const slide of childPosts) {
      if (!slide || typeof slide !== 'object') continue;
      const s = slide as Record<string, unknown>;
      const u = (s.public_url ?? s.displayUrl ?? s.imageUrl ?? s.thumbnailUrl ?? s.url) as string | undefined;
      if (u) out.push(u);
    }
    return out;
  }
  return [];
}

function CarouselViewer({ slides, coverUrl, permalink, onClose }: { slides: string[]; coverUrl: string | null; permalink: string | null; onClose: () => void }) {
  const allImages = slides.length > 0 ? slides : (coverUrl ? [coverUrl] : []);
  const [idx, setIdx] = useState(0);
  const [errored, setErrored] = useState(false);

  useEffect(() => { setErrored(false); }, [idx]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(allImages.length - 1, i + 1));
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [allImages.length, onClose]);

  if (!allImages.length) return null;
  const cur = allImages[Math.min(idx, allImages.length - 1)];

  // Detect non-mirrored URLs (still pointing at instagram CDN) so user knows why they may be dead.
  const isMirrored = /supabase\.co/i.test(cur);

  const overlay = (
    <div
      onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 36, height: 36, borderRadius: 18, cursor: 'pointer', fontSize: 'var(--fs-h2)', lineHeight: 1, fontFamily: 'inherit' }}
      >×</button>

      {idx > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.max(0, i - 1)); }}
          style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 44, height: 44, borderRadius: 22, cursor: 'pointer', fontSize: 'var(--fs-h1)', lineHeight: 1, fontFamily: 'inherit' }}
        >‹</button>
      )}
      {idx < allImages.length - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.min(allImages.length - 1, i + 1)); }}
          style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 44, height: 44, borderRadius: 22, cursor: 'pointer', fontSize: 'var(--fs-h1)', lineHeight: 1, fontFamily: 'inherit' }}
        >›</button>
      )}

      {!errored ? (
        <SignedImg
          src={cur}
          alt=""
          onClick={(e) => e.stopPropagation()}
          onError={() => setErrored(true)}
          referrerPolicy="no-referrer"
          style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', background: '#111' }}
        />
      ) : (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 480, padding: 24, borderRadius: 10, background: 'rgba(20,22,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', color: '#dde', textAlign: 'center', fontFamily: 'inherit' }}
        >
          <div style={{ fontSize: 'var(--fs-h1)', marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', marginBottom: 6 }}>Image unavailable</div>
          <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', lineHeight: 1.5, marginBottom: 14 }}>
            {isMirrored
              ? 'Mirrored copy failed to load (storage error?).'
              : 'Original Instagram CDN URL has expired. Run "🖼 Mirror All Carousels" in admin to save permanent copies.'}
          </div>
          {permalink && (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 6, background: 'rgba(74,158,255,0.15)', color: '#bcd0ff', textDecoration: 'none', fontSize: 'var(--fs-small)', border: '1px solid rgba(74,158,255,0.3)' }}
            >
              View on Instagram ↗
            </a>
          )}
        </div>
      )}

      <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: '#dde', fontSize: 'var(--fs-body)', fontFamily: 'inherit', background: 'rgba(0,0,0,0.5)', padding: '4px 12px', borderRadius: 12 }}>
        {idx + 1} / {allImages.length}{!isMirrored && slides.length > 0 ? ' · ⚠ unmirrored' : ''}
      </div>
    </div>
  );

  // Render via portal so the overlay escapes any ancestor with transform/filter/will-change
  // (the parent modal sets backdrop-filter, which creates a containing block for `position: fixed`).
  return createPortal(overlay, document.body);
}

function PostThumb({ p }: { p: PostRow }) {
  const { t } = useTranslation('admin');
  const src = p.thumb_public_url ?? null;
  const [imgErrored, setImgErrored] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const slides = p.post_type === 'carousel' ? extractCarouselSlides(p.child_posts) : [];
  const isCarousel = p.post_type === 'carousel';
  const showImg = !!src && !imgErrored;
  const isVideoType = p.post_type === 'reel' || p.post_type === 'video';
  const hasVideo = !!p.video_public_url;
  const captionPreview = p.caption ? p.caption.replace(/\s+/g, ' ').trim() : '';
  const dateLabel = p.posted_at ? new Date(p.posted_at).toLocaleDateString(currentLocale()) : '—';

  // If playing, swap thumbnail for inline video player
  if (playing && p.video_public_url) {
    return (
      <div style={{ position: 'relative', aspectRatio: '9 / 16', background: '#000', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(245,183,0,0.4)' }}>
        <SignedVideo
          src={p.video_public_url}
          autoPlay controls
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPlaying(false); }}
          style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 'var(--fs-caption)', fontFamily: 'inherit' }}
        >✕</button>
      </div>
    );
  }

  const card = (
    <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'rgba(0,0,0,0.4)', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(15,45,100,0.3)' }}>
      {showImg ? (
        <SignedImg
          src={src!}
          alt=""
          loading="lazy"
          onError={() => setImgErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%', padding: '10px 10px 24px',
          background: 'linear-gradient(135deg, rgba(15,45,100,0.25), rgba(74,158,255,0.08))',
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        }}>
          <div style={{ fontSize: 'var(--fs-micro)', color: '#778', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            {t('creator.noPreview', { defaultValue: 'No preview' })}
          </div>
          <div style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: '#bcd0ff', marginBottom: 6 }}>{dateLabel}</div>
          <div style={{ fontSize: 'var(--fs-caption)', color: '#bbc', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
            {captionPreview || t('creator.noCaption', { defaultValue: '(no caption)' })}
          </div>
        </div>
      )}

      {/* Top-left chips: post type + Apify badge */}
      <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {p.post_type && p.post_type !== 'image' && (
          <span style={postTypeChipStyle(p.post_type)}>
            {p.post_type === 'reel'     ? t('creator.post.reel',     { defaultValue: 'REEL' })
              : p.post_type === 'video'    ? t('creator.post.video',    { defaultValue: 'VIDEO' })
              : p.post_type === 'carousel' ? t('creator.post.carousel', { defaultValue: 'CAROUSEL' })
              : p.post_type.toUpperCase()}
          </span>
        )}
        {hasVideo && (
          <span style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 5px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#4caf50', letterSpacing: 0.5, border: '1px solid #4caf50' }}>
            AP
          </span>
        )}
        {p.is_coauthored && (
          <span title={p.owner_handle ? `Collab · @${p.owner_handle}` : 'Collaboration'}
            style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 5px', borderRadius: 4, background: 'rgba(80,200,120,0.85)', color: '#062', letterSpacing: 0.3 }}>
            🤝 {p.owner_handle ? `@${p.owner_handle}` : 'collab'}
          </span>
        )}
      </div>

      {/* Play button overlay for mirrored videos */}
      {isVideoType && hasVideo && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true); }}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.25)', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: 'var(--fs-h1)', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.8))', lineHeight: 1 }}>▶</span>
        </button>
      )}

      {/* Bottom bar: stats */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-micro)', color: '#dde' }}>
        <span>
          {p.views != null ? `👁 ${p.views.toLocaleString()} · ` : ''}
          ♥ {(p.likes ?? 0).toLocaleString()}
          {p.comments != null ? ` · 💬 ${p.comments.toLocaleString()}` : ''}
        </span>
        <span>{dateLabel}</span>
      </div>
    </div>
  );

  // Carousel → click opens inline viewer (don't leave the page).
  if (isCarousel) {
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarouselOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCarouselOpen(true); } }}
          style={{ cursor: 'pointer' }}
        >
          {card}
        </div>
        {carouselOpen && (
          <CarouselViewer
            slides={slides}
            coverUrl={src}
            permalink={p.permalink}
            onClose={() => setCarouselOpen(false)}
          />
        )}
      </>
    );
  }

  // If video is mirrored, click plays inline; otherwise open permalink
  if (isVideoType && hasVideo) return card;
  return p.permalink ? (
    <a href={p.permalink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{card}</a>
  ) : card;
}

function postTypeChipStyle(postType: string): React.CSSProperties {
  // Color per type — keeps the grid scannable at a glance.
  // Reel = orange (matches IG's brand), carousel = blue, video = purple.
  const color = postType === 'reel' ? '#f5b700'
              : postType === 'carousel' ? '#4a9eff'
              : postType === 'video' ? '#9B5DE5'
              : '#9aa';
  return {
    fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4,
    background: 'rgba(0,0,0,0.55)', color,
    letterSpacing: 0.5, border: `1px solid ${color}`,
  };
}

function StoryThumb({ s }: { s: StoryRow }) {
  const { t } = useTranslation('admin');
  // Prefer the permanent mirrored copy; fall back to the raw IG CDN URL (alive
  // for days, with cross-origin CORS headers) so freshly-scraped-but-not-yet-
  // mirrored stories still render. onError below drops to the placeholder if a
  // raw URL ever truly dies. Native IG aspect ratio is 9:16.
  const src = s.thumb_public_url ?? s.thumbnail_url ?? null;
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);
  const showImg = !!src && !errored;
  const captionPreview = s.caption ? s.caption.replace(/\s+/g, ' ').trim() : '';
  const takenLabel = s.taken_at ? new Date(s.taken_at).toLocaleString() : '—';
  const isVideo = (s.media_type ?? '').toLowerCase().includes('video');
  const expired = s.expires_at ? new Date(s.expires_at).getTime() < Date.now() : false;
  // Source: served from our Supabase mirror, or straight from the IG CDN.
  const mirrored = !!src && /supabase\.co/i.test(src);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => showImg && setOpen(true)}
        onKeyDown={(e) => { if (showImg && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); } }}
        title={mirrored ? 'Mirrored copy (Supabase storage)' : 'Live from Instagram CDN (not yet mirrored)'}
        style={{
          position: 'relative', aspectRatio: '9 / 16',
          background: 'rgba(0,0,0,0.4)', borderRadius: 8, overflow: 'hidden',
          border: '1px solid rgba(155,93,229,0.35)',
          opacity: expired ? 0.7 : 1,
          cursor: showImg ? 'zoom-in' : 'default',
        }}
      >
        {showImg ? (
          <SignedImg
            src={src!}
            alt=""
            loading="lazy"
            onError={() => setErrored(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%', padding: '10px 10px 26px',
            background: 'linear-gradient(135deg, rgba(155,93,229,0.18), rgba(15,45,100,0.25))',
            display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          }}>
            <div style={{ fontSize: 'var(--fs-micro)', color: '#9aa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
              {t('creator.noPreview', { defaultValue: 'No preview' })}
            </div>
            <div style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: '#d8c4ff', marginBottom: 6 }}>
              {takenLabel}
            </div>
            <div style={{
              fontSize: 'var(--fs-caption)', color: '#bbc', lineHeight: 1.35,
              display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', flex: 1,
            }}>
              {captionPreview || t('creator.noCaption', { defaultValue: '(no caption)' })}
            </div>
          </div>
        )}
        <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#9B5DE5', letterSpacing: 0.5, border: '1px solid #9B5DE5' }}>
            {isVideo
              ? t('creator.story.video', { defaultValue: 'STORY · VIDEO' })
              : t('creator.story.photo', { defaultValue: 'STORY' })}
          </span>
          {showImg && (
            mirrored ? (
              <span title="Mirrored to Supabase storage" style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#4caf50', letterSpacing: 0.5, border: '1px solid #4caf50' }}>
                {t('creator.story.mirrored', { defaultValue: '💾 SAVED' })}
              </span>
            ) : (
              <span title="Served live from Instagram CDN — not yet mirrored" style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#f5b700', letterSpacing: 0.5, border: '1px solid #f5b700' }}>
                {t('creator.story.live', { defaultValue: '🔗 LIVE' })}
              </span>
            )
          )}
          {expired && (
            <span style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#888', letterSpacing: 0.5, border: '1px solid #555' }}>
              {t('creator.story.expired', { defaultValue: 'EXPIRED' })}
            </span>
          )}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-micro)', color: '#dde' }}>
          <span>{s.view_count != null ? `👁 ${s.view_count.toLocaleString()}` : '—'}</span>
          <span>{s.taken_at ? new Date(s.taken_at).toLocaleDateString(currentLocale()) : '—'}</span>
        </div>
      </div>
      {open && <StoryViewer s={s} src={src!} mirrored={mirrored} isVideo={isVideo} onClose={() => setOpen(false)} />}
    </>
  );
}

// Full-screen story viewer (portal). Plays the video for video stories when the
// media_url is available; otherwise shows the enlarged cover image.
function StoryViewer({ s, src, mirrored, isVideo, onClose }: { s: StoryRow; src: string; mirrored: boolean; isVideo: boolean; onClose: () => void }) {
  const { t } = useTranslation('admin');
  // Two independent failures: the raw IG video (media_url) may fail to play in the
  // browser even though it's reachable — when it does we MUST fall back to the
  // (working, mirrored) poster image, not jump to the error box. Only show the
  // error box if the image ALSO fails.
  const [videoErrored, setVideoErrored] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);
  const canPlayVideo = isVideo && !!s.media_url && !videoErrored;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);

  const caption = s.caption ? s.caption.replace(/\s+/g, ' ').trim() : '';
  const overlay = (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 36, height: 36, borderRadius: 18, cursor: 'pointer', fontSize: 'var(--fs-h2)', lineHeight: 1, fontFamily: 'inherit' }}
      >×</button>

      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '92vw' }}>
        {canPlayVideo ? (
          <SignedVideo
            src={s.media_url!}
            poster={src}
            autoPlay controls loop
            onError={() => setVideoErrored(true)}
            style={{ maxWidth: '92vw', maxHeight: '80vh', borderRadius: 10, background: '#111', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
          />
        ) : (src && !imgErrored) ? (
          <SignedImg
            src={src}
            alt=""
            onError={() => setImgErrored(true)}
            style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 10, background: '#111', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
          />
        ) : (
          <div style={{ maxWidth: 420, padding: 24, borderRadius: 10, background: 'rgba(20,22,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', color: '#dde', textAlign: 'center', fontFamily: 'inherit' }}>
            <div style={{ fontSize: 'var(--fs-h1)', marginBottom: 8 }}>⚠️</div>
            <div style={{ fontSize: 'var(--fs-body)', color: '#9aa' }}>
              {mirrored ? 'Mirrored copy failed to load.' : 'The live Instagram URL is no longer available — re-scrape to refresh.'}
            </div>
          </div>
        )}

        {/* Source + caption footer */}
        <div style={{ maxWidth: 'min(92vw, 520px)', textAlign: 'center', color: '#cdd', fontSize: 'var(--fs-small)', fontFamily: 'inherit' }}>
          <span style={{ display: 'inline-block', fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 8px', borderRadius: 10, marginBottom: caption ? 8 : 0, color: mirrored ? '#4caf50' : '#f5b700', border: `1px solid ${mirrored ? '#4caf50' : '#f5b700'}`, background: 'rgba(0,0,0,0.4)' }}>
            {mirrored
              ? t('creator.story.sourceMirrored', { defaultValue: '💾 Mirrored copy (Supabase storage)' })
              : t('creator.story.sourceLive', { defaultValue: '🔗 Live from Instagram CDN (not yet mirrored)' })}
          </span>
          {caption && <div style={{ lineHeight: 1.5, color: '#bbc' }}>{caption}</div>}
        </div>
      </div>
    </div>
  );
  return createPortal(overlay, document.body);
}

function LanguageEditor({
  selected, onSet,
}: {
  selected: string[];
  onSet: (next: string[]) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const options: Array<{ code: string; bg: string; fg: string; border: string }> = [
    { code: 'ru', bg: 'rgba(74,158,255,0.10)', fg: '#bcd0ff', border: 'rgba(74,158,255,0.35)' },
    { code: 'ky', bg: 'rgba(245,183,0,0.10)', fg: '#f5b700', border: 'rgba(245,183,0,0.35)' },
    { code: 'en', bg: 'rgba(155,93,229,0.10)', fg: '#b18cf0', border: 'rgba(155,93,229,0.35)' },
  ];
  const toggle = async (code: string) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    setBusy(true);
    await onSet(next);
    setBusy(false);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => {
        const active = selected.includes(o.code);
        return (
          <button
            key={o.code}
            type="button"
            disabled={busy}
            onClick={() => toggle(o.code)}
            style={{
              padding: '4px 12px',
              borderRadius: 12,
              fontSize: 'var(--fs-small)',
              fontWeight: 'var(--fw-semibold)',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              background: active ? o.bg : 'transparent',
              color: active ? o.fg : '#778',
              border: `1px solid ${active ? o.border : 'rgba(255,255,255,0.12)'}`,
              cursor: busy ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >{o.code}</button>
        );
      })}
    </div>
  );
}

function NicheEditor({
  allNiches, selected, onSet,
}: {
  allNiches: NicheRow[];
  selected: NicheRow[];
  onSet: (next: string[]) => void | Promise<void>;
}) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const selectedIds = selected.map((n) => n.id);
  const remaining = allNiches.filter((n) => !selectedIds.includes(n.id));

  const remove = async (nicheId: string) => {
    setBusy(true);
    await onSet(selectedIds.filter((id) => id !== nicheId));
    setBusy(false);
  };
  const add = async (niche: NicheRow) => {
    if (selectedIds.includes(niche.id)) return;
    setBusy(true);
    await onSet([...selectedIds, niche.id]);
    setBusy(false);
    setOpen(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {selected.map((n) => (
          <span key={n.id} style={{ padding: '4px 8px 4px 10px', borderRadius: 12, background: 'rgba(155,93,229,0.10)', color: '#b18cf0', fontSize: 'var(--fs-small)', border: '1px solid rgba(155,93,229,0.30)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {n.name_ru}
            <button
              type="button" onClick={() => remove(n.id)} disabled={busy}
              aria-label={t('creator.removeNiche', { defaultValue: 'Remove' })}
              style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: busy ? 'wait' : 'pointer', fontSize: 'var(--fs-body)', lineHeight: 1, padding: 0 }}
            >×</button>
          </span>
        ))}
        {selected.length === 0 && (
          <span style={{ fontSize: 'var(--fs-body)', color: '#778' }}>{t('creator.noNiches', { defaultValue: 'No niches assigned yet.' })}</span>
        )}
      </div>

      {remaining.length > 0 && (
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <button
            type="button" onClick={() => setOpen((v) => !v)} disabled={busy}
            style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(155,93,229,0.08)', color: '#b18cf0', fontSize: 'var(--fs-small)', border: '1px dashed rgba(155,93,229,0.35)', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
          >+ {t('creator.addNiche', { defaultValue: 'Add niche' })}</button>
          {open && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--bg-elev, #161b22)', border: '1px solid rgba(155,93,229,0.35)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', zIndex: 10, minWidth: 220 }}>
              {remaining.map((n) => (
                <button
                  key={n.id} type="button" onClick={() => add(n)} disabled={busy}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', color: '#dde', fontSize: 'var(--fs-body)', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
                >{n.name_ru}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AmbassadorEditor({
  brands, ambassadorIds, rawHandles, onSet,
}: {
  brands: BrandRow[];
  ambassadorIds: string[];
  rawHandles: string[];
  onSet: (next: string[]) => void | Promise<void>;
}) {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const searchQ = useSupabaseQuery<BrandRow[]>(
    ['admin-creator-brand-search', search],
    async () => {
      const term = search.trim();
      if (term.length < 2) return { data: [], error: null };
      return supabase
        .from('brands')
        .select('id, name')
        .ilike('name', `%${term}%`)
        .order('name', { ascending: true })
        .limit(20);
    },
  );

  const remove = async (brandId: string) => {
    setBusy(true);
    await onSet(ambassadorIds.filter((id) => id !== brandId));
    setBusy(false);
  };
  const add = async (brand: BrandRow) => {
    if (ambassadorIds.includes(brand.id)) return;
    setBusy(true);
    await onSet([...ambassadorIds, brand.id]);
    setBusy(false);
    setSearch('');
  };

  const candidates = (searchQ.data ?? []).filter((b) => !ambassadorIds.includes(b.id));
  const unresolved = rawHandles.filter((h) => {
    return !brands.some((b) => b.name?.toLowerCase().includes(h));
  });

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {brands.map((b) => (
          <span key={b.id} style={{ padding: '4px 8px 4px 10px', borderRadius: 12, background: 'rgba(245,183,0,0.12)', color: '#f5b700', fontSize: 'var(--fs-small)', border: '1px solid rgba(245,183,0,0.3)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {b.name}
            <button
              type="button" onClick={() => remove(b.id)} disabled={busy}
              aria-label={t('creator.removeAmbassador', { defaultValue: 'Remove' })}
              style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: busy ? 'wait' : 'pointer', fontSize: 'var(--fs-body)', lineHeight: 1, padding: 0 }}
            >×</button>
          </span>
        ))}
        {brands.length === 0 && (
          <span style={{ fontSize: 'var(--fs-body)', color: '#778' }}>{t('creator.noAmbassadors', { defaultValue: 'Not an ambassador of any brand yet.' })}</span>
        )}
      </div>

      <div style={{ position: 'relative', maxWidth: 360 }}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('creator.addAmbassador', { defaultValue: 'Search brand to add…' })}
          style={{ width: '100%' }}
        />
        {search.trim().length >= 2 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-elev, #161b22)', border: '1px solid rgba(15,45,100,0.5)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', zIndex: 10 }}>
            {searchQ.isLoading ? (
              <div style={{ padding: 10, color: '#778', fontSize: 'var(--fs-small)' }}>…</div>
            ) : candidates.length === 0 ? (
              <div style={{ padding: 10, color: '#778', fontSize: 'var(--fs-small)' }}>{t('creator.noBrandHits', { defaultValue: 'No brands match.' })}</div>
            ) : candidates.map((b) => (
              <button
                key={b.id} type="button" onClick={() => add(b)} disabled={busy}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', color: '#dde', fontSize: 'var(--fs-body)', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {unresolved.length > 0 && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'rgba(15,45,100,0.08)', border: '1px dashed rgba(15,45,100,0.4)' }}>
          <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa', marginBottom: 4 }}>
            {t('creator.detectedInBio', { defaultValue: 'Detected in bio (no brand match yet)' })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {unresolved.map((h) => (
              <a
                key={h} href={`https://www.instagram.com/${h}/`} target="_blank" rel="noopener noreferrer"
                style={{ padding: '3px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', color: '#bcd0ff', fontSize: 'var(--fs-caption)', border: '1px solid rgba(255,255,255,0.12)', textDecoration: 'none' }}
              >@{h}</a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
